import { Clipboard, Banknote, FileCheck, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const StepsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const steps = [
    {
      step: '1',
      title: 'Consulta Inicial',
      description: 'Cuéntanos qué trámite necesitas realizar',
      icon: Clipboard,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      step: '2',
      title: 'Cotización Instantánea',
      description: 'Recibe precio y tiempo estimado al instante',
      icon: Banknote,
      color: 'from-purple-500 to-pink-500'
    },
    {
      step: '3',
      title: 'Documentación Digital',
      description: 'Sube tus documentos de forma segura y encriptada',
      icon: FileCheck,
      color: 'from-orange-500 to-red-500'
    },
    {
      step: '4',
      title: 'Gestión Completa',
      description: 'Nos encargamos de todo el proceso de principio a fin',
      icon: Sparkles,
      color: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-pink-200 to-orange-200 rounded-full opacity-20 blur-3xl translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 shadow-md">
            <span className="text-purple-600 font-semibold text-sm">PROCESO SIMPLE</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            ¿Cómo Funciona Nuestro{" "}
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Proceso?
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            4 pasos simples para obtener tus trámites sin complicaciones
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 -translate-y-1/2 z-0"></div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
            {steps.map((process, index) => (
              <ProcessStep
                key={index}
                {...process}
                index={index}
                isVisible={isVisible}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ProcessStep = ({ step, title, description, color, index, isVisible }) => {
  const delay = `${index * 150}ms`;

  return (
    <div
      className={`relative bg-white rounded-2xl p-1 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      }`}
      style={{ transitionDelay: delay }}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>

      <div className="relative z-10 text-center">
        <div className={`relative w-20 h-20 bg-gradient-to-br ${color} rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-6 shadow-lg transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500`}>
          <span className="relative z-10">{step}</span>
          <div className="absolute inset-0 bg-white/20 rounded-full blur-xl"></div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
          {title}
        </h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
      </div>

      <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl ${color} opacity-0 group-hover:opacity-10 rounded-full blur-2xl transition-opacity duration-500`}></div>
    </div>
  );
};

export default StepsSection;